/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ExportContext} from '../../../types/redux';
import {
  adminUsersViewMovedResultsSelector,
  userMoveErrorsSelector,
  userMoveResultSelector,
  userMoveSelectedUsersSelector,
  userMoveShowDialogSelector
} from './selector';
import {moveUsers, showMovedUsers} from './state';

/**
  *
  * Custom hook for showing Snackbar for Moving Users.
  *
  * ```typescript
  *
  *  import {useUserMove} from '@ion/applications/src/pages/admin/users;
  *
  *  const {movedUsers, showMoveUser, selectedUsersToMove} = useUserMove();
  *
  * ```
  */
export const useUserMove = () => {
  const dispatch                      = useDispatch();
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const movedUsers                    = useSelector(userMoveResultSelector);
  const selectedUsersToMove           = useSelector(userMoveSelectedUsersSelector);
  const movedUsersErrors              = useSelector(userMoveErrorsSelector);
  const showMoveUser                  = useSelector(userMoveShowDialogSelector);
  const viewMovedUsers                = useSelector(adminUsersViewMovedResultsSelector);
  const {pending}                     = useSelector(({exporter}) => exporter.current);
  const isExporting                   = pending[ExportContext.movedDevices] === true;

  useEffect(() => {
    if (movedUsers && !showMoveUser) {
      setShowSuccess(true);
    }
  }, [movedUsers]);

  const onCloseSnack = useCallback(() => {
    setShowSuccess(false);
    dispatch(moveUsers(null));
  }, []);

  const onViewRecords = useCallback(() => {
    dispatch(showMovedUsers());
    setShowSuccess(false);
  }, []);

  const onCancelViewMovedUsersDialog = useCallback(() => {
    dispatch(showMovedUsers(false));
  }, []);

  return {
    isExporting,
    movedUsers,
    showSuccess,
    onCloseSnack,
    onViewRecords,
    showMoveUser,
    viewMovedUsers,
    movedUsersErrors,
    selectedUsersToMove,
    onCancelViewMovedUsersDialog
  };
};

export default useUserMove;
